/**
 * Step header: the title, the guide ⓘ and the one-line subtitle.
 *
 * Explanations live ONLY behind the ⓘ (owner ruling 2026-09-10) — no
 * explanation column, no persistent "show explanations" toggle. The optional
 * header switch is the single entry into quick create (wired in Task B7).
 */

import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import { WithGuideHelp } from '@modules/ui-components/dialogs';
import type { GuideService } from '@modules/firebase-core/services/GuideService';
import { STRATEGY_LIST } from '@test-kit/selectors/strategy-list';
import type { WizardStepDef } from './wizardSteps';

export interface WizardStepHeaderProps {
  step: WizardStepDef;
  guideService: GuideService;
  headerSwitch?: { label: string; onClick: () => void };
}

export const WizardStepHeader: React.FC<WizardStepHeaderProps> = ({
  step,
  guideService,
  headerSwitch,
}) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
      <Typography variant="h5" data-testid={STRATEGY_LIST.wizardStepTitle}>
        {step.title}
      </Typography>
      <WithGuideHelp
        guideKey={step.guideKey}
        guideService={guideService}
        position="inline-end"
        iconSize="small"
        showPreviewOnHover={false}
        helpLabel="About this step"
        helpTestId={STRATEGY_LIST.wizardStepHelp}
      >
        {null}
      </WithGuideHelp>
      {headerSwitch && (
        <>
          <Box sx={{ flex: '1 1 auto' }} />
          <Button
            variant="text"
            size="small"
            onClick={headerSwitch.onClick}
            // Invariant 15: text-level primary affordances use `primary.dark`
            // on the dialog's off-white paper.
            sx={{ textTransform: 'none', color: 'primary.dark', whiteSpace: 'nowrap' }}
            data-testid={STRATEGY_LIST.wizardSwitchMode}
          >
            {headerSwitch.label}
          </Button>
        </>
      )}
    </Box>
    <Typography variant="body2" color="text.secondary">
      {step.subtitle}
    </Typography>
  </Box>
);

WizardStepHeader.displayName = 'WizardStepHeader';
